import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import {
  FEEDBACK_FIELDS,
  RADIO_FEEDBACK_MIN_SECONDS,
  RADIO_FEEDBACK_REWARD_JGK,
  STANDOUT_OPTIONS,
  buildRadioFeedbackPayload,
  formatRadioFeedbackTime,
  getFeedbackUnlockProgress,
  getRadioFeedbackArtistName,
  getRadioFeedbackTrackId,
  getRadioFeedbackTrackTitle,
  getRewardMessage,
  initialFeedbackForm,
  isFeedbackComplete,
  isFeedbackUnlocked,
} from './hitLabRadioFeedback';

function FeedbackChoiceGroup({ field, value, onChange }) {
  return (
    <fieldset className="hitlab-radio-feedback-group">
      <legend>{field.label}</legend>
      <div role="radiogroup" aria-label={field.label}>
        {field.options.map(([optionValue, label]) => (
          <label
            key={optionValue}
            className={`hitlab-radio-feedback-choice ${
              value === optionValue ? 'is-selected' : ''
            }`}
          >
            <input
              type="radio"
              name={field.name}
              value={optionValue}
              checked={value === optionValue}
              onChange={() => onChange(field.name, optionValue)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function HitLabRadioFeedbackSheet({
  open = false,
  track = null,
  player = null,
  elapsed = 0,
  duration = 0,
  source = 'joe_galaxy_radio',
  route = '/hitlab/radio',
  loginHref = '/login?from=%2Fhitlab%2Fradio',
  variant = 'full',
  triggerRef,
  onClose,
  onLogin,
  onSubmitted,
}) {
  const dialogRef = useRef(null);
  const initialFocusDoneRef = useRef(false);
  const [feedbackForm, setFeedbackForm] = useState(initialFeedbackForm);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState(null);
  const [feedbackError, setFeedbackError] = useState('');

  const trackId = getRadioFeedbackTrackId(track);
  const trackTitle = getRadioFeedbackTrackTitle(track);
  const artistName = getRadioFeedbackArtistName(track);
  const coverArtUrl = String(track?.coverArtUrl || '').trim();
  const feedbackEligible = Boolean(trackId) && track?.feedbackEligible !== false;
  const feedbackUnlocked = isFeedbackUnlocked({ elapsed, duration });
  const feedbackUnlockProgress = getFeedbackUnlockProgress({
    elapsed,
    duration,
  });
  const complete = isFeedbackComplete(feedbackForm);

  const resetFeedbackState = useCallback(() => {
    setFeedbackForm(initialFeedbackForm);
    setFeedbackSubmitting(false);
    setFeedbackResult(null);
    setFeedbackError('');
  }, []);

  const closeSheet = useCallback(() => {
    setFeedbackSubmitting(false);
    onClose?.();
    window.setTimeout(() => {
      triggerRef?.current?.focus?.();
    }, 0);
  }, [onClose, triggerRef]);

  useEffect(() => {
    if (!open) return;
    resetFeedbackState();
  }, [open, resetFeedbackState, trackId]);

  useEffect(() => {
    if (!open || !feedbackEligible) {
      initialFocusDoneRef.current = false;
      return undefined;
    }
    if (initialFocusDoneRef.current) return undefined;
    initialFocusDoneRef.current = true;
    const focusTimer = window.setTimeout(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (dialog.contains(document.activeElement)) return;
      dialog.focus();
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [feedbackEligible, open]);

  useEffect(() => {
    if (!open || !feedbackEligible) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeSheet();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeSheet, feedbackEligible, open]);

  if (!open || !feedbackEligible) return null;

  const updateFeedbackForm = (field, value) => {
    setFeedbackForm((current) => ({
      ...current,
      [field]: field === 'comment' ? String(value || '').slice(0, 500) : value,
    }));
  };

  const submitFeedback = async () => {
    if (!player?.token || !feedbackUnlocked || !complete) return;

    setFeedbackSubmitting(true);
    setFeedbackError('');
    setFeedbackResult(null);
    try {
      const payload = buildRadioFeedbackPayload({
        track,
        elapsed,
        duration,
        feedbackForm,
        source,
        route,
      });
      const response = await axiosConn.post('hitlab/radio/feedback', payload, {
        headers: {
          Authorization: `Bearer ${player.token}`,
        },
      });
      const nextFeedback = response?.data?.data?.feedback || null;
      setFeedbackResult(nextFeedback);
      onSubmitted?.(nextFeedback);
    } catch (err) {
      setFeedbackError(
        err?.response?.data?.message ||
          'Feedback could not be saved. Please try again.',
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const backdropClassName = [
    'hitlab-radio-feedback-backdrop',
    variant === 'dock' ? 'hitlab-radio-feedback-backdrop--dock' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const sheetClassName = [
    'hitlab-radio-feedback-sheet',
    variant === 'dock' ? 'hitlab-radio-feedback-sheet--dock' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={backdropClassName}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeSheet();
      }}
    >
      <section
        className={sheetClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hitlab-radio-feedback-title"
        tabIndex="-1"
        ref={dialogRef}
      >
        <button
          type="button"
          className="hitlab-radio-feedback-close"
          onClick={closeSheet}
          aria-label="Close feedback"
        >
          Close
        </button>

        <div className="hitlab-radio-feedback-track">
          {coverArtUrl ? (
            <img
              src={coverArtUrl}
              alt={`Cover art for ${trackTitle} by ${artistName}`}
              width="96"
              height="96"
            />
          ) : (
            <div className="hitlab-radio-feedback-art-fallback" aria-hidden="true">
              JG
            </div>
          )}
          <div>
            <p className="hitlab-kicker">Fan action signal</p>
            <h2 id="hitlab-radio-feedback-title">
              Give feedback. Earn {RADIO_FEEDBACK_REWARD_JGK} JGK.
            </h2>
            <p>
              {trackTitle} by {artistName}
            </p>
          </div>
        </div>

        {!player?.token ? (
          <div className="hitlab-radio-feedback-auth">
            <h3>Sign in to give feedback and earn JGK.</h3>
            <p>
              Your signal helps records earn their next move inside Joe Galaxy
              Radio.
            </p>
            <button type="button" className="hitlab-primary-btn" onClick={onLogin}>
              Sign In
            </button>
            <Link className="hitlab-secondary-link" to={loginHref}>
              Continue to login
            </Link>
          </div>
        ) : !feedbackUnlocked ? (
          <div className="hitlab-radio-feedback-locked">
            <h3>Listen a little longer to unlock rewarded feedback.</h3>
            <p>
              {formatRadioFeedbackTime(elapsed)} /{' '}
              {formatRadioFeedbackTime(RADIO_FEEDBACK_MIN_SECONDS)}
            </p>
            <div
              className="hitlab-radio-feedback-unlock"
              role="progressbar"
              aria-label="Rewarded feedback unlock progress"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.round(feedbackUnlockProgress)}
            >
              <span style={{ width: `${feedbackUnlockProgress}%` }} />
            </div>
          </div>
        ) : feedbackResult ? (
          <div className="hitlab-radio-feedback-result" role="status">
            <h3>{getRewardMessage(feedbackResult)}</h3>
            <p>
              Your signal helps records earn their next move inside Joe Galaxy
              Radio.
            </p>
          </div>
        ) : (
          <form
            className="hitlab-radio-feedback-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitFeedback();
            }}
          >
            <p>
              Your signal helps records earn their next move inside Joe Galaxy
              Radio.
            </p>
            {FEEDBACK_FIELDS.map((field) => (
              <FeedbackChoiceGroup
                key={field.name}
                field={field}
                value={feedbackForm[field.name]}
                onChange={updateFeedbackForm}
              />
            ))}

            <details className="hitlab-radio-feedback-extra">
              <summary>Add optional detail</summary>
              <fieldset className="hitlab-radio-feedback-group">
                <legend>What stood out?</legend>
                <div role="radiogroup" aria-label="What stood out?">
                  {STANDOUT_OPTIONS.map(([optionValue, label]) => (
                    <label
                      key={optionValue}
                      className={`hitlab-radio-feedback-choice ${
                        feedbackForm.standoutReason === optionValue
                          ? 'is-selected'
                          : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="standoutReason"
                        value={optionValue}
                        checked={feedbackForm.standoutReason === optionValue}
                        onChange={() =>
                          updateFeedbackForm('standoutReason', optionValue)
                        }
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="hitlab-radio-feedback-comment">
                Optional comment
                <textarea
                  value={feedbackForm.comment}
                  maxLength="500"
                  onChange={(event) =>
                    updateFeedbackForm('comment', event.target.value)
                  }
                  placeholder="A short note for the signal."
                />
              </label>
            </details>

            {feedbackError ? (
              <div className="hitlab-radio-error" role="alert">
                {feedbackError}
              </div>
            ) : null}

            <button
              type="submit"
              className="hitlab-primary-btn"
              disabled={!complete || feedbackSubmitting}
            >
              {feedbackSubmitting ? 'Submitting' : 'Submit Feedback + Earn JGK'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
